/**
 * Extract a recording's audio track and re-encode it as 16 kHz mono 16-bit WAV — entirely in the
 * browser. A screen recording (video) is hundreds of MB and can't stream through the API's request
 * limit; its audio, at 16 kHz mono, is a fraction of the size and is exactly what the transcription
 * service wants. We never upload the video itself.
 */
export async function extractAudioToWav(file: File, targetRate = 16000): Promise<File> {
  const AC: typeof AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AC) throw new Error('This browser can’t decode audio.');
  const bytes = await file.arrayBuffer();
  const ctx = new AC();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(bytes.slice(0));
  } finally {
    void ctx.close();
  }
  // Resample to mono targetRate offline (fast — not real-time).
  const frames = Math.max(1, Math.ceil(decoded.duration * targetRate));
  const off = new OfflineAudioContext(1, frames, targetRate);
  const src = off.createBufferSource();
  src.buffer = decoded;
  src.connect(off.destination);
  src.start();
  const rendered = await off.startRendering();
  const wav = encodeWav(rendered.getChannelData(0), targetRate);
  const name = file.name.replace(/\.[^.]+$/, '') + '.wav';
  return new File([wav], name, { type: 'audio/wav' });
}

/** Minimal 16-bit PCM mono WAV encoder. */
function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const n = samples.length;
  const out = new ArrayBuffer(44 + n * 2);
  const dv = new DataView(out);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); dv.setUint32(4, 36 + n * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, sampleRate, true); dv.setUint32(28, sampleRate * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
  str(36, 'data'); dv.setUint32(40, n * 2, true);
  let o = 44;
  for (let i = 0; i < n; i++) { const s = Math.max(-1, Math.min(1, samples[i]!)); dv.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true); o += 2; }
  return out;
}
